import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getCanonicalUrl, needsRedirect, getRedirectUrl, shouldNoIndex } from '../utils/canonicalUtils';
import { isKeyPage } from '../utils/SEOUtils';

/**
 * Enhanced Canonical Manager Component
 * Handles:
 * - Canonical URL management
 * - Client-side redirects for trailing slashes and query params
 * - NoIndex tags for pages with query parameters
 * - Duplicate canonical tag prevention
 * - Explicit index directives for key pages
 */
const EnhancedCanonicalManager: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname, search } = location;
  
  // Handle client-side redirects
  useEffect(() => {
    if (needsRedirect(pathname, search)) {
      const redirectUrl = getRedirectUrl(pathname, search);
      navigate(redirectUrl, { replace: true });
    }
  }, [pathname, search, navigate]);
  
  // Get canonical URL
  const canonicalUrl = getCanonicalUrl(pathname, search);
  
  // Check if this is the main blog page without query parameters
  const isMainBlogPage = pathname === '/blog' && !search;
  
  // Determine if noindex should be added
  // Allow shouldNoIndex to determine if blog pages with params should be noindexed
  const shouldAddNoIndex = shouldNoIndex(pathname, search) && pathname !== '/';
  
  // Check if this is a key page that should have explicit index directive
  // Only include the main blog page without parameters
  const isKeyPagePath = isKeyPage(pathname) && (pathname !== '/blog' || !search);
  
  // Clean up duplicate canonical tags
  useEffect(() => {
    const cleanupCanonicalTags = () => {
      const canonicalTags = document.querySelectorAll('link[rel="canonical"]');
      
      if (canonicalTags.length > 1) {
        // Keep only the first canonical tag
        for (let i = 1; i < canonicalTags.length; i++) {
          canonicalTags[i].remove();
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`[EnhancedCanonicalManager] Removed ${canonicalTags.length - 1} duplicate canonical tags`);
        }
      }
      
      // Update the canonical URL if it doesn't match
      if (canonicalTags.length > 0) {
        const currentCanonical = canonicalTags[0] as HTMLLinkElement;
        if (currentCanonical.href !== canonicalUrl) {
          currentCanonical.href = canonicalUrl;
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`[EnhancedCanonicalManager] Updated canonical URL to: ${canonicalUrl}`);
          }
        }
      }
    };
    
    // Initial cleanup
    cleanupCanonicalTags();
    
    // Set up observer to watch for dynamically added canonical tags
    const observer = new MutationObserver(cleanupCanonicalTags);
    observer.observe(document.head, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['rel', 'href']
    });
    
    return () => observer.disconnect();
  }, [canonicalUrl]);
  
  return (
    <Helmet>
      <link rel="canonical" href={canonicalUrl} />
      {shouldAddNoIndex && <meta name="robots" content="noindex, follow" />}
      {isKeyPagePath && <meta name="robots" content="index, follow" />}
    </Helmet>
  );
};

export default EnhancedCanonicalManager; 